/**
 * Raises a toast when a thread the user is not watching reaches an ending.
 *
 * Mounted once, beside the rest of the app shell. The decision itself is in
 * `threadNotifications.logic`; this hook only supplies the current threads and
 * remembers what it has already said, so a reconnect or a projection replay
 * that hands back the same state stays quiet.
 */
import { scopeThreadRef, scopedThreadKey } from "@afar-research/client-runtime/environment";
import { useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { toastManager } from "../components/ui/toast";
import { resolveThreadRouteRef } from "../threadRoutes";
import { useThreadShells } from "../state/entities";
import {
  planThreadNotifications,
  THREAD_NOTIFICATION_MESSAGES,
  threadNotificationKind,
  type ThreadNotificationState,
} from "../threadNotifications.logic";
import { useClientSettings } from "./useSettings";

export function useThreadNotifications(): void {
  const enabled = useClientSettings((settings) => settings.inAppNotificationsEnabled);
  const threads = useThreadShells();
  const routeThreadRef = useParams({
    strict: false,
    select: (params) => resolveThreadRouteRef(params),
  });
  const activeThreadKey = routeThreadRef ? scopedThreadKey(routeThreadRef) : null;
  // What has already been said, by thread. Never rendered, so a ref keeps it
  // out of the dependency graph that decides when to announce.
  const announced = useRef(new Map<string, string>());

  const states = useMemo(
    (): readonly ThreadNotificationState[] =>
      threads.map((thread) => ({
        threadKey: scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
        title: thread.title,
        kind: threadNotificationKind(thread),
        at:
          thread.latestTurn?.completedAt ??
          thread.latestTurn?.startedAt ??
          thread.updatedAt ??
          null,
      })),
    [threads],
  );

  useEffect(() => {
    // Seeding rather than announcing: whatever was already finished when the
    // app opened is history, not news.
    if (announced.current.size === 0 && states.length > 0) {
      for (const state of states) {
        if (state.at !== null) announced.current.set(state.threadKey, state.at);
      }
      return;
    }
    const notifications = planThreadNotifications({
      threads: states,
      announced: announced.current,
      activeThreadKey,
      enabled,
    });
    for (const notification of notifications) {
      announced.current.set(notification.threadKey, notification.at);
      toastManager.add({
        type: notification.kind === "failed" ? "error" : "info",
        title: notification.title,
        description: `This thread ${THREAD_NOTIFICATION_MESSAGES[notification.kind]}.`,
      });
    }
  }, [activeThreadKey, enabled, states]);
}
